import { makeAutoObservable, values } from 'mobx'

import { VisualElement, VisualElementsClient } from 'api/clients/VisualElementClient'

import { type RootStore } from 'stores/RootStore'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

export class VisualElementsStore {
  rootStore: RootStore
  elements: Record<number, VisualElement> = {}

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  getElement(id: number) {
    return this.elements[id]
  }

  fetchElements = async () => {
    const res = await VisualElementsClient.getAllElements()

    if (res.type === 'success') {
      for (const element of res.data) {
        this.addElement(element)
      }
    }
  }

  addElement = (element: VisualElement) => {
    this.elements[element.id] = element
  }

  bulkAdd = (elements: VisualElement[]) => {
    elements.forEach(this.addElement)
  }

  get all() {
    return values(this.elements)
  }

  getByDimensions = (dimensions: { height: number; width: number }) => {
    return this.all.filter((element) => element.width === dimensions.width && element.height === dimensions.height)
  }

  getBySourceId = (source_id: number) => {
    return this.all.filter((element) => element.source_id === source_id)
  }

  createElement = async (element: Omit<VisualElement, 'id' | 'updated_at'>) => {
    const res = await VisualElementsClient.createElement(element)

    if (res.type !== 'success') {
      showToastMessage('There was an error creating the element', 'error')
      return
    }

    this.elements[res.data.id] = res.data

    return res.data
  }

  deleteElement = async (id: number) => {
    const res = await VisualElementsClient.deleteElement(id)

    if (res.type !== 'success') {
      showToastMessage('There was an error deleting the element', 'error')
      return
    }

    delete this.elements[id]
  }

  updateElement = async (id: number, updatedElement: Partial<VisualElement>) => {
    const existingElement = this.elements[id]

    if (!existingElement) {
      showToastMessage('There was an error updating the element', 'error')
      return
    }

    this.elements[id] = { ...existingElement, ...updatedElement }

    const res = await VisualElementsClient.updateElement(id, updatedElement)

    if (res.type === 'success') {
      this.elements[id] = res.data
      return res.data
    } else {
      showToastMessage('There was an error updating the element', 'error')
    }
  }
}
